import { useState, useMemo } from "react";
import Modal from "../Modal";
import Button from "../Button";
import Checkbox from "../Checkbox";
import { Icon } from "../Icon";
import { ActivityFragment, EventFragment } from "../../__generated__/graphql";
import { formatDateWithDay, formatTimeRange } from "../../lib/utils";
import { generateItineraryPDF } from "./pdfGenerator";
import { FaFileExport } from "react-icons/fa6";
import { useUser } from "../../contexts/UserContext/hooks/useUser";

interface ExportScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: EventFragment;
}

export default function ExportScheduleModal({
  isOpen,
  onClose,
  event,
}: ExportScheduleModalProps) {
  const { currentOrganization, userInfos } = useUser();
  
  const [selectedActivityIds, setSelectedActivityIds] = useState<Set<string>>(
    new Set()
  );
  const [isExporting, setIsExporting] = useState(false);

  const activities = event.activities ?? [];
  const allSelected =
    selectedActivityIds.size === activities.length && activities.length > 0;

  // Group activities by date
  const activitiesByDate = useMemo(() => {
    const grouped: Record<string, ActivityFragment[]> = {};

    activities.forEach((activity) => {
      const dateKey = formatDateWithDay(activity.startDate);
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(activity);
    });

    // Sort activities within each date
    Object.keys(grouped).forEach((date) => {
      grouped[date].sort(
        (a, b) =>
          new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
      );
    });

    return grouped;
  }, [activities]);

  // Sort dates chronologically
  const sortedDates = useMemo(() => {
    return Object.keys(activitiesByDate).sort((a, b) => {
      const dateA = new Date(activitiesByDate[a][0].startDate);
      const dateB = new Date(activitiesByDate[b][0].startDate);
      return dateA.getTime() - dateB.getTime();
    });
  }, [activitiesByDate]);

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedActivityIds(new Set());
    } else {
      setSelectedActivityIds(new Set(activities.map((a) => a.id)));
    }
  };

  const handleToggleActivity = (activityId: string) => {
    const newSelected = new Set(selectedActivityIds);
    if (newSelected.has(activityId)) {
      newSelected.delete(activityId);
    } else {
      newSelected.add(activityId);
    }
    setSelectedActivityIds(newSelected);
  };

  const handleExport = async () => {
    if (selectedActivityIds.size === 0) return;

    setIsExporting(true);
    try {
      const selectedActivities = activities.filter((a) =>
        selectedActivityIds.has(a.id)
      );
      await generateItineraryPDF(event, selectedActivities, {
        userInfo: userInfos
          ? {
              firstName: userInfos.firstName,
              lastName: userInfos.lastName,
              email: userInfos.email,
              phoneNumber: userInfos.phoneNumber,
            }
          : null,
        organizationInfo: currentOrganization
          ? {
              name: currentOrganization.name,
            }
          : null,
      });
      onClose();
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    setSelectedActivityIds(new Set());
    onClose();
  };

  return (
    <Modal
      isModalOpen={isOpen}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-2">
          <FaFileExport className="w-5 h-5 text-accent" />
          Exporter le planning
        </div>
      }
      description="Sélectionnez les activités à inclure dans l'export PDF"
      className="w-full max-w-lg"
    >
      <div className="flex flex-col gap-4">
        {/* Select All */}
        <div
          className="flex items-center gap-3 p-3 bg-card rounded-md cursor-pointer hover:bg-card/80 transition-colors"
          onClick={handleSelectAll}
        >
          <Checkbox checked={allSelected} />
          <span className="font-medium">
            Tout sélectionner ({activities.length} activités)
          </span>
        </div>

        {/* Activities grouped by date */}
        <div className="max-h-80 overflow-y-auto custom-scrollbar flex flex-col gap-3">
          {sortedDates.map((date) => (
            <div key={date} className="flex flex-col gap-2">
              <div className="text-sm font-semibold text-secondary px-1">
                {date}
              </div>
              {activitiesByDate[date].map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-center gap-3 p-3 bg-subCard rounded-md cursor-pointer hover:bg-subCard/80 transition-colors"
                  onClick={() => handleToggleActivity(activity.id)}
                >
                  <Checkbox checked={selectedActivityIds.has(activity.id)} />
                  <div
                    className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                    style={{ backgroundColor: activity.color }}
                  >
                    <Icon icon={activity.icon} className="w-4 h-4" />
                  </div>
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="font-medium truncate">
                      {activity.title}
                    </span>
                    <span className="text-sm text-secondary">
                      {formatTimeRange(activity.startDate, activity.endDate)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Export Button */}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={handleClose}>
            Annuler
          </Button>
          <Button
            onClick={handleExport}
            isLoading={isExporting}
            disabled={selectedActivityIds.size === 0}
          >
            Exporter ({selectedActivityIds.size})
          </Button>
        </div>
      </div>
    </Modal>
  );
}
